import React, { ReactElement, useMemo, useRef, useState } from "react";
import { Button, Modal } from "antd";
import style from "./index.module.scss";
import { BaseQuestionType, QuestionType } from "service/exercise";
import { errorMessage } from "service/service";
import Form from "rc-field-form";
import {
    typeIdsAtom,
    useSubTopicType
} from "service/stores/Exercise/ExerciseTypes";
import { ValidateErrorEntity } from "rc-field-form/es/interface";
import usePromiseCallback from "utils/usePromiseCallback";
import { useUpdateEffect } from "react-use";
import FieldFillOptions from "./components/FieldFillOptions";
import SubTopics from "./components/SubTopics";
import FieldContentText from "./components/FieldContentText";
import FieldAnalysis from "./components/FieldAnalysis";
import FieldOptions from "./components/FieldOptions";
import FieldJudgeOptions from "./components/FieldJudgeOptions";
import FieldScore from "./components/FieldScore";
import FieldTypes from "./components/FieldTypes";
import { useAtomValue } from "jotai";
import { ResultMessage } from "components/ResultMessage";
import SubmitError from "./components/SubmitError";

interface AddTopicModalProps {
    title: React.ReactNode;
    visible?: boolean;
    onOk: (data: QuestionType) => Promise<void> | void;
    onCancel?: () => void;
    types?: BaseQuestionType[];
    initialValue?: QuestionType;
    afterClose?: () => void;
}

export default function AddTopicModal({
    title,
    visible,
    onOk,
    onCancel,
    types,
    initialValue,
    afterClose
}: AddTopicModalProps) {
    const allTypeIds = useAtomValue(typeIdsAtom);
    const defaultTopicType =
        initialValue?.queTypeId ||
        types?.[0] ||
        allTypeIds[0] ||
        BaseQuestionType.SingleChoice; // 默认选中的题型
    const [queTypeId, setQueTypeId] =
        useState<BaseQuestionType>(defaultTopicType);

    const subTopicTypes = useSubTopicType(queTypeId);

    const [form] = Form.useForm<QuestionType>();

    const saveTypeRef = useRef(false); // true保存并关闭 false保存并继续添加
    const [errors, setErrors] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);

    const isMixedType = queTypeId >= BaseQuestionType.Mixed;

    // 切换题型重置内容
    useUpdateEffect(() => form.resetFields(), [queTypeId]);

    // 提交表单
    const onFinish = usePromiseCallback(async (question: QuestionType) => {
        try {
            setLoading(true);
            setErrors([]);
            await onOk({ ...question, queTypeId: queTypeId as any });

            if (saveTypeRef.current) {
                onCancel?.();
            } else {
                form.resetFields();
                scrollToTop();
            }
        } catch (e) {
            ResultMessage.error(errorMessage(e));
        } finally {
            setLoading(false);
        }
    });

    // 内容区域滚动到最上
    function scrollToTop() {
        document
            .querySelector(`.${style.root} .ant-modal-body`)
            ?.scrollTo(0, 0);
    }

    // 表单校验失败
    function onFinishFailed({
        errorFields
    }: ValidateErrorEntity<QuestionType>) {
        setErrors(errorFields);
        ResultMessage.error(<SubmitError />, { width: 400 });
    }

    // 保存并添加
    async function onSaveAndContinue() {
        saveTypeRef.current = false;
        form.submit();
    }

    // 保存并关闭
    async function onSave() {
        saveTypeRef.current = true;
        form.submit();
    }

    // 获取某一表单项的错误信息
    function getError(name: string) {
        for (let i in errors) {
            for (let j in errors[i].name) {
                if (errors[i].name[j] === name) {
                    return errors[i].errors[j];
                }
            }
        }
    }

    // 如果修改过内容, 关闭前给出提示
    function onClose() {
        if (form.isFieldsTouched()) {
            Modal.confirm({
                content: "页面输入内容未保存，您确定离开此页面吗？",
                onOk: onCancel
            });
        } else {
            onCancel?.();
        }
    }

    const footer = (
        <div>
            <Button onClick={onClose}>取消</Button>
            {!initialValue && (
                <Button
                    type="primary"
                    ghost
                    onClick={onSaveAndContinue}
                    loading={!saveTypeRef.current && loading}>
                    继续添加
                </Button>
            )}
            <Button
                type="primary"
                onClick={onSave}
                loading={saveTypeRef.current && loading}>
                保存
            </Button>
        </div>
    );

    // 如果title中包含onClose属性, 重置onClose的行为
    const modalTitle = useMemo(() => {
        if (typeof title === "string") return title;
        return React.cloneElement(React.Children.only(title) as ReactElement, {
            onClose
        });
    }, [title]);

    return (
        <Modal
            title={modalTitle}
            visible={visible}
            onCancel={onClose}
            footer={footer}
            width={940}
            maskClosable={false}
            centered
            className={style.root}
            afterClose={afterClose}
            destroyOnClose>
            <Form
                form={form}
                onFinish={onFinish}
                onFinishFailed={onFinishFailed}
                initialValues={initialValue}>
                <Form.Field name="id" />
                <Form.Field name="paperId" />
                <Form.Field name="sort" />

                <FieldTypes
                    disabled={!!initialValue}
                    types={types || allTypeIds}
                    value={queTypeId}
                    onChange={setQueTypeId}
                    showManage={!types}
                    form={form}
                />

                <FieldContentText />

                {queTypeId === BaseQuestionType.Subjective && <FieldAnalysis />}

                {[
                    BaseQuestionType.SingleChoice,
                    BaseQuestionType.MultipleChoice
                ].includes(queTypeId) && (
                    <FieldOptions queTypeId={queTypeId} getError={getError} />
                )}

                {queTypeId === BaseQuestionType.Judge && <FieldJudgeOptions />}

                {queTypeId === BaseQuestionType.Fill && <FieldFillOptions />}

                {isMixedType && (
                    <SubTopics subTypes={subTopicTypes || []} form={form} />
                )}

                {!isMixedType && <FieldScore />}
            </Form>
        </Modal>
    );
}
